import '../styles/LoginIdentityConsole.css';
import React, { useEffect, useMemo, useReducer, useRef } from 'react';


const MODES = {
  establish: {
    key: 'establish',
    label: 'First-time account',
    note: 'Empty fields, no pre-filled identity. A first-time account is issued to a consignment sender or recipient.',
    submit: 'Establish identity',
    pending: 'Establishing identity',
  },
  verify: {
    key: 'verify',
    label: 'Returning verification',
    note: 'Verify a returning account. The administrator, senders and recipients all verify through this same console.',
    submit: 'Verify identity',
    pending: 'Verifying identity',
  },
};

function initialReturnDestination() {
  if (typeof window === 'undefined' || !window.location) {
    return '';
  }
  const next = new URLSearchParams(window.location.search).get('next');
  return next && next.trim() ? next.trim() : '';
}

function destinationForRole(role, requested) {
  if (requested) {
    return requested;
  }
  if (role === 'administrator') {
    return '/admin';
  }
  return '/track-shipment';
}

function initialState() {
  return {
    mode: 'verify',
    email: '',
    credential: '',
    reveal: false,
    fieldErrors: { email: '', credential: '' },
    formError: '',
    submitStatus: 'idle',
    outcome: null,
    restartCount: 0,
    returnDestination: initialReturnDestination(),
  };
}

function reducer(state, action) {
  switch (action.type) {
    case 'mode':
      return {
        ...state,
        mode: action.mode,
        email: action.mode === 'establish' ? '' : state.email,
        credential: '',
        reveal: false,
        fieldErrors: { email: '', credential: '' },
        formError: '',
        submitStatus: 'idle',
        outcome: null,
      };
    case 'email':
      return {
        ...state,
        email: action.value,
        fieldErrors: { ...state.fieldErrors, email: '' },
        formError: '',
      };
    case 'credential':
      return {
        ...state,
        credential: action.value,
        fieldErrors: { ...state.fieldErrors, credential: '' },
        formError: '',
      };
    case 'reveal':
      return { ...state, reveal: !state.reveal };
    case 'submit':
      return {
        ...state,
        submitStatus: 'submitting',
        fieldErrors: { email: '', credential: '' },
        formError: '',
        outcome: null,
      };
    case 'field-error':
      return {
        ...state,
        submitStatus: 'idle',
        fieldErrors: { ...state.fieldErrors, [action.field]: action.message },
      };
    case 'form-error':
      return { ...state, submitStatus: 'idle', formError: action.message };
    case 'success':
      return {
        ...state,
        submitStatus: 'idle',
        credential: '',
        reveal: false,
        outcome: action.outcome,
      };
    case 'restart':
      return {
        ...state,
        credential: '',
        reveal: false,
        fieldErrors: { email: '', credential: '' },
        formError: '',
        submitStatus: 'idle',
        outcome: null,
        restartCount: state.restartCount + 1,
      };
    default:
      return state;
  }
}

function InProgressArc() {
  return (
    <svg
      className="lic-arc"
      width="20"
      height="20"
      viewBox="0 0 30 30"
      aria-hidden="true"
      focusable="false"
    >
      <circle
        className="lic-arc-track"
        cx="15"
        cy="15"
        r="12"
        fill="none"
        strokeWidth="2"
      />
      <circle
        className="lic-arc-value"
        cx="15"
        cy="15"
        r="12"
        fill="none"
        strokeWidth="2"
      />
    </svg>
  );
}

function LoginIdentityConsole({ onSubmit }) {
  const [state, dispatch] = useReducer(reducer, undefined, initialState);
  const emailRef = useRef(null);
  const credentialRef = useRef(null);

  // Returning verification starts hydrated when identity is carried in the URL.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.location) {
      return;
    }
    const carried = new URLSearchParams(window.location.search).get('email');
    if (carried && carried.trim()) {
      dispatch({ type: 'email', value: carried.trim() });
    }
  }, []);

  const mode = MODES[state.mode];
  const submitting = state.submitStatus === 'submitting';

  const outcomeDestination = state.outcome
    ? destinationForRole(state.outcome.role, state.returnDestination)
    : '';

  const destinationLabel = useMemo(() => {
    if (state.returnDestination) {
      return state.returnDestination;
    }
    return 'determined from your account role';
  }, [state.returnDestination]);

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting) {
      return;
    }

    const email = state.email.trim();
    const credential = state.credential.trim();

    if (!email) {
      dispatch({
        type: 'field-error',
        field: 'email',
        message: 'Enter the email identity attached to this account.',
      });
      if (emailRef.current) emailRef.current.focus();
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      dispatch({
        type: 'field-error',
        field: 'email',
        message: 'That email identity is not in a valid address form.',
      });
      if (emailRef.current) emailRef.current.focus();
      return;
    }
    if (!credential) {
      dispatch({
        type: 'field-error',
        field: 'credential',
        message:
          mode.key === 'establish'
            ? 'Set a credential to seal this account.'
            : 'Enter the credential on record for this account.',
      });
      if (credentialRef.current) credentialRef.current.focus();
      return;
    }

    dispatch({ type: 'submit' });

    try {
      if (typeof onSubmit !== 'function') {
        dispatch({ type: 'form-error', message: 'Identity submission is not available in this preview.' });
        return;
      }

      const values = {
        email,
        credential,
        mode: mode.key,
      };
      const result = await onSubmit(values);

      if (result?.success === true) {
        dispatch({
          type: 'success',
          outcome: {
            kind: mode.key === 'establish' ? 'established' : 'verified',
            email: result.email || email,
            role: result.role,
          },
        });
        return;
      }

      if (result?.field && result?.message) {
        dispatch({
          type: 'field-error',
          field: result.field,
          message: result.message,
        });
        return;
      }

      dispatch({
        type: 'form-error',
        message: result?.message || 'Identity could not be submitted. Check your details and try again.',
      });
    } catch (err) {
      dispatch({
        type: 'form-error',
        message: err?.message || 'Identity service did not respond. Your entries are preserved — retry when the console is reachable.',
      });
    }
  }

  const emailError = state.fieldErrors.email;
  const credentialError = state.fieldErrors.credential;

  return (
    <section
      className="lic-root"
      aria-labelledby="lic-wordmark"
      id="login-identity-console"
    >
      <div className="lic-grid">
        <div className="lic-plate">
          <div className="lic-console-line">
            <span className="lic-eyebrow">Identity console</span>
            <span className="lic-ref">FF · CONSIGNMENT · ACCESS</span>
          </div>

          <div className="lic-lockup">
            <span className="lic-lockup-pip" aria-hidden="true" />
            <h1 className="lic-wordmark" id="lic-wordmark">
              Fast Forward
            </h1>
          </div>
          <span className="lic-sublock">Deliver Services</span>

          <p className="lic-statement">
            An account is what keeps a shipment&rsquo;s record attached to its
            owner &mdash; the waybill, the route and every timestamp stay bound to
            the participant who holds them.
          </p>

          {/* ---------- IdentityModeSwitch ---------- */}
          <div
            className="lic-modes"
            role="tablist"
            aria-label="Identity mode"
          >
            {Object.values(MODES).map((entry) => {
              const active = entry.key === state.mode;
              return (
                <button
                  key={entry.key}
                  type="button"
                  role="tab"
                  id={'lic-mode-' + entry.key}
                  aria-selected={active}
                  aria-controls="lic-credential-form"
                  tabIndex={active ? 0 : -1}
                  className={'lic-mode' + (active ? ' is-active' : '')}
                  onClick={() => dispatch({ type: 'mode', mode: entry.key })}
                >
                  {entry.label}
                </button>
              );
            })}
          </div>
          <p className="lic-mode-note">{mode.note}</p>

          {/* ---------- RuledCredentialForm + FieldErrorBaselines ---------- */}
          <form
            className="lic-form"
            id="lic-credential-form"
            role="tabpanel"
            aria-labelledby={'lic-mode-' + state.mode}
            onSubmit={handleSubmit}
            noValidate
          >
            <div className={'lic-row' + (emailError ? ' is-invalid' : '')}>
              <label className="lic-label" htmlFor="lic-email">
                <span>Email identity</span>
                <span className="lic-label-unit">account of record</span>
              </label>
              <span className="lic-input-wrap">
                <input
                  id="lic-email"
                  ref={emailRef}
                  className={'lic-input' + (emailError ? ' has-error' : '')}
                  type="email"
                  name="email"
                  inputMode="email"
                  autoComplete="email"
                  spellCheck="false"
                  placeholder="name@fastforward.example"
                  value={state.email}
                  onChange={(e) =>
                    dispatch({ type: 'email', value: e.target.value })
                  }
                  aria-invalid={emailError ? 'true' : undefined}
                  aria-describedby="lic-email-baseline"
                />
              </span>
              <p
                className={'lic-baseline' + (emailError ? ' is-error' : '')}
                id="lic-email-baseline"
                role={emailError ? 'alert' : undefined}
              >
                {emailError ? (
                  <>
                    <span className="lic-baseline-mark" aria-hidden="true">
                      Error
                    </span>
                    <span>{emailError}</span>
                  </>
                ) : (
                  <span>
                    Bound to a consignment sender, recipient or the administrator.
                  </span>
                )}
              </p>
            </div>

            <div
              className={'lic-row' + (credentialError ? ' is-invalid' : '')}
            >
              <label className="lic-label" htmlFor="lic-credential">
                <span>Credential</span>
                <span className="lic-label-unit">
                  {mode.key === 'establish' ? 'new' : 'on record'}
                </span>
              </label>
              <span className="lic-input-wrap">
                <input
                  id="lic-credential"
                  ref={credentialRef}
                  className={
                    'lic-input with-reveal' +
                    (credentialError ? ' has-error' : '')
                  }
                  type={state.reveal ? 'text' : 'password'}
                  name="credential"
                  autoComplete={
                    mode.key === 'establish' ? 'new-password' : 'current-password'
                  }
                  spellCheck="false"
                  placeholder="••••••••"
                  value={state.credential}
                  onChange={(e) =>
                    dispatch({ type: 'credential', value: e.target.value })
                  }
                  aria-invalid={credentialError ? 'true' : undefined}
                  aria-describedby="lic-credential-baseline"
                />
                <button
                  type="button"
                  className="lic-reveal"
                  aria-pressed={state.reveal}
                  onClick={() => dispatch({ type: 'reveal' })}
                >
                  {state.reveal ? 'Hide' : 'Show'}
                </button>
              </span>
              <p
                className={
                  'lic-baseline' + (credentialError ? ' is-error' : '')
                }
                id="lic-credential-baseline"
                role={credentialError ? 'alert' : undefined}
              >
                {credentialError ? (
                  <>
                    <span className="lic-baseline-mark" aria-hidden="true">
                      Error
                    </span>
                    <span>{credentialError}</span>
                  </>
                ) : (
                  <span>
                    Held only against this console; never shown in the clear.
                  </span>
                )}
              </p>
            </div>

            <div
              className={
                'lic-form-status' +
                (state.formError
                  ? ' is-error'
                  : state.outcome
                  ? ' is-success'
                  : '')
              }
              role="status"
              aria-live="polite"
            >
              {state.formError && (
                <>
                  <span className="lic-form-status-mark" aria-hidden="true">
                    Halt
                  </span>
                  <span>{state.formError}</span>
                </>
              )}
              {!state.formError && state.outcome && (
                <>
                  <span className="lic-form-status-mark" aria-hidden="true">
                    Verified
                  </span>
                  <span>
                    {state.outcome.kind === 'established'
                      ? 'Identity established for '
                      : 'Identity verified for '}
                    {state.outcome.email} ({state.outcome.role}). Continue to{' '}
                    <a href={outcomeDestination}>{outcomeDestination}</a>.
                  </span>
                </>
              )}
            </div>

            <div className="lic-submit-row">
              <button
                type="submit"
                className="lic-submit"
                disabled={submitting}
                aria-busy={submitting}
              >
                {submitting && <InProgressArc />}
                <span>{submitting ? mode.pending : mode.submit}</span>
              </button>
              <p className="lic-submit-note">
                Destination: <span>{destinationLabel}</span>
              </p>
            </div>
          </form>

          {/* ---------- VerificationRecoveryRow ---------- */}
          <div className="lic-recovery">
            <button
              type="button"
              className="lic-restart"
              onClick={() => dispatch({ type: 'restart' })}
            >
              Restart verification
            </button>
            <p className="lic-destination">
              Returning to{' '}
              <span className="lic-destination-value">
                {state.returnDestination || 'your tracking surface'}
              </span>
              {state.restartCount > 0
                ? ' — console reset, place kept.'
                : ' once identity is verified.'}
            </p>
          </div>

          <div className="lic-foot-baseline">
            <span>Fast Forward Deliver Services</span>
            <span>Sole administrator · hellomcdonald8@gmail.com</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default LoginIdentityConsole;